/**
 * Форматує число з суфіксами k, kk, kkk, kkkk
 *
 * @param num - Число для форматування
 * @param ignore_units - Список одиниць, які потрібно пропустити (наприклад, ["k"] пропустить рівень "k")
 * @returns Відформатований рядок
 *
 * @example
 * format_num(1500) // "1.5k"
 * format_num(1500, ["k"]) // "1 500"
 * format_num(15000000) // "15kk"
 * format_num(1500000000) // "1.5kkk"
 * format_num(1500000000000, ["k", "kk", "kkk"]) // "1.5kkkk"
 */

// Floor to 1 decimal place (never rounds up)
const floor1 = (n: number): number => Math.floor(n * 10) / 10;

export function format_num(
  num: number,
  ignore_units: ("k" | "kk" | "kkk")[] = []
): string {
  const absNum = Math.abs(num);
  const sign = num < 0 ? "-" : "";

  // Якщо число менше 1000, форматуємо з максимум 1 знаком після крапки
  if (absNum < 1000) {
    if (absNum % 1 === 0) {
      return `${sign}${absNum}`;
    }
    // Обмежуємо до 1 знаку після крапки, прибираємо зайві нулі
    return `${sign}${parseFloat(floor1(absNum).toFixed(1))}`;
  }

  // k: 1 000
  if (absNum < 1_000_000) {
    if (ignore_units.includes("k")) {
      // Якщо ігноруємо "k", форматуємо з пробілами для тисяч
      const floored = Math.floor(absNum);
      const formatted = floored.toLocaleString("en-US").replace(/,/g, " ");
      return `${sign}${formatted}`;
    }
    const k = absNum / 1000;
    // Якщо ціле число - показуємо без десяткових
    if (k % 1 === 0) {
      return `${sign}${k}k`;
    }
    // Інакше показуємо з одним знаком після коми (floor, не round)
    return `${sign}${floor1(k).toFixed(1)}k`;
  }

  // kk: 1 000 000
  if (absNum < 1_000_000_000) {
    if (ignore_units.includes("kk")) {
      // Якщо ігноруємо "kk", але не "k", використовуємо "k"
      if (!ignore_units.includes("k")) {
        const k = absNum / 1000;
        if (k % 1 === 0) {
          return `${sign}${k}k`;
        }
        return `${sign}${floor1(k).toFixed(1)}k`;
      }
      // Якщо ігноруємо обидва, форматуємо з пробілами
      const floored = Math.floor(absNum);
      const formatted = floored.toLocaleString("en-US").replace(/,/g, " ");
      return `${sign}${formatted}`;
    }
    const kk = absNum / 1_000_000;
    if (kk % 1 === 0) {
      return `${sign}${kk}kk`;
    }
    return `${sign}${floor1(kk).toFixed(1)}kk`;
  }

  // kkk: 1 000 000 000
  if (absNum < 1_000_000_000_000) {
    if (ignore_units.includes("kkk")) {
      // Якщо ігноруємо "kkk", але не "kk", використовуємо "kk"
      if (!ignore_units.includes("kk")) {
        const kk = absNum / 1_000_000;
        if (kk % 1 === 0) {
          return `${sign}${kk}kk`;
        }
        return `${sign}${floor1(kk).toFixed(1)}kk`;
      }
      // Якщо ігноруємо "kkk" і "kk", але не "k", використовуємо "k"
      if (!ignore_units.includes("k")) {
        const k = absNum / 1000;
        if (k % 1 === 0) {
          return `${sign}${k}k`;
        }
        return `${sign}${floor1(k).toFixed(1)}k`;
      }
      // Якщо ігноруємо всі, форматуємо з пробілами
      const floored = Math.floor(absNum);
      const formatted = floored.toLocaleString("en-US").replace(/,/g, " ");
      return `${sign}${formatted}`;
    }
    const kkk = absNum / 1_000_000_000;
    if (kkk % 1 === 0) {
      return `${sign}${kkk}kkk`;
    }
    return `${sign}${floor1(kkk).toFixed(1)}kkk`;
  }

  // kkkk: 1 000 000 000 000
  if (ignore_units.includes("kkk")) {
    // Якщо ігноруємо "kkk", але не "kk", використовуємо "kk"
    if (!ignore_units.includes("kk")) {
      const kk = absNum / 1_000_000;
      if (kk % 1 === 0) {
        return `${sign}${kk}kk`;
      }
      return `${sign}${floor1(kk).toFixed(1)}kk`;
    }
    // Якщо ігноруємо "kkk" і "kk", але не "k", використовуємо "k"
    if (!ignore_units.includes("k")) {
      const k = absNum / 1000;
      if (k % 1 === 0) {
        return `${sign}${k}k`;
      }
      return `${sign}${floor1(k).toFixed(1)}k`;
    }
    // Якщо ігноруємо всі, форматуємо з пробілами
    const floored = Math.floor(absNum);
    const formatted = floored.toLocaleString("en-US").replace(/,/g, " ");
    return `${sign}${formatted}`;
  }
  const kkkk = absNum / 1_000_000_000_000;
  if (kkkk % 1 === 0) {
    return `${sign}${kkkk}kkkk`;
  }
  return `${sign}${floor1(kkkk).toFixed(1)}kkkk`;
}

